import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import UsersPanel from './UsersPanel';
import styles from './index.module.css';

const planOf = (u) => u?.subscription?.product_name || u?.plan_name || 'Free';

// A tile's bucket ↔ the backend `creation.bucket`. 'flat' tiles are uncapped
// offers with no included/after split, so they take any discounted creation.
const bucketMatches = (tileBucket, cBucket) => {
  if (tileBucket === 'flat') return cBucket === 'included' || cBucket === 'after';
  return (tileBucket || 'included') === cBucket;
};

// Decide whether a managed user belongs to a given plan tile.
// The backend tags each user (managerUsersList) with `creation` = their latest
// completed account-creation record: { plan_id, bucket:'included'|'after'|'none',
// created_ts }. A user belongs to a tile when it's the same plan, the same
// bucket, and the record falls in the current billing cycle (created_ts >=
// cycleStartTs) — the exact same rows the tile's count is built from.
// Falls back to an all-time plan-name match only for users with no record.
const matchesTile = (u, plan, cycleStartTs) => {
  if (!plan) return false;
  // Backend deployed: every user carries a `creation` key (object or null). A
  // null record means the user belongs to no offer tile (don't fall through to
  // the name match, or legacy users would show under every same-named tile).
  if (u && Object.prototype.hasOwnProperty.call(u, 'creation')) {
    const c = u.creation;
    if (!c || c.plan_id == null) return false;
    const samePlan = String(c.plan_id) === String(plan.planId);
    const inCycle = cycleStartTs == null
      || (c.created_ts != null && Number(c.created_ts) >= cycleStartTs);
    return samePlan && inCycle && bucketMatches(plan.bucket, c.bucket);
  }
  // Pre-deploy fallback (response has no `creation` field at all): match by
  // plan name so the modal keeps working until the backend change ships.
  return planOf(u) === plan.name;
};

// Modal that grows out of the clicked plan card and lists the users created
// under that plan tile — reusing the Manage Users panel so every row keeps the
// same actions + rules (Finish Setup / Send-Resend Email / Copy link / Access).
// `origin` is the clicked card's viewport rect (grow-from-card animation);
// `plan` carries the tile's name, badge, count + detail.
const PlanUsersModal = ({
  plan, origin, onClose, canViewViewers, blockCreateUser, onCreateUserBlocked, cycleStart,
}) => {
  // Current billing-cycle start as a unix timestamp (seconds), to gate creation
  // records to "this cycle" — matches the backend `creation.created_ts` unit.
  const cycleStartTs = (() => {
    if (!cycleStart) return null;
    const ms = Date.parse(cycleStart);
    return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
  })();
  // Offset the modal's start position/scale from the clicked card's centre so it
  // appears to grow out of it.
  const start = (() => {
    if (!origin || typeof window === 'undefined') return { opacity: 0, scale: 0.6 };
    const cx = origin.left + origin.width / 2;
    const cy = origin.top + origin.height / 2;
    return {
      opacity: 0,
      scale: 0.18,
      x: cx - window.innerWidth / 2,
      y: cy - window.innerHeight / 2,
    };
  })();

  // Same count the tile shows (e.g. "0/30" or "∞"), plus its detail line.
  const countText = plan
    ? [plan.detail, plan.count].filter((s) => s && String(s).trim()).join(' · ')
    : '';

  return (
    <AnimatePresence>
      {plan && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={styles.planModal}
            role="dialog"
            aria-modal="true"
            style={{ width: 'min(880px, 95vw)' }}
            initial={start}
            animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
            exit={start}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.planModalHead}>
              <div>
                <span className={styles.planModalName}>{plan.name}</span>
                {plan.badge ? <span className={styles.planModalBadge}>{plan.badge}</span> : null}
                {plan.note ? <span className={styles.planModalBadge}>{plan.note}</span> : null}
              </div>
              <button type="button" className={styles.planModalClose} onClick={onClose} aria-label="Close">
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>
            <span className={styles.planModalSub}>
              {countText ? `${countText} this cycle` : 'Users on this plan'}
            </span>

            <div className={styles.planModalList}>
              <UsersPanel
                hideHeader
                rowFilter={(u) => matchesTile(u, plan, cycleStartTs)}
                emptyLabel="No users created on this tile this cycle yet."
                canViewViewers={canViewViewers}
                blockCreateUser={blockCreateUser}
                onCreateUserBlocked={onCreateUserBlocked}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlanUsersModal;
